import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { restCacheConfig } from '../config/axios';
import { QUERY_KEYS } from '../hooks/hooksUtils';
import { useInfiniteQueryData } from '../hooks/useInfiniteQueryData';
import { useQueryData } from '../hooks/useQueryData';
import { useUserStore } from '../stores/UserStore';
import { ACTION_TYPES } from '../types/postsTypings';
import { USER_ROLES } from '../types/userTypings';
import { RestService } from './RestService';
import { createAction, isFollowing } from './UsersService';
import { extendFormData, MULTIPART_HEADERS } from './Utils';

export const fetchGroups = ({ pageParam }) => {
  return RestService.get(`/groups?page=${pageParam}`);
};

export const useGroupsData = () => {
  return useInfiniteQueryData([QUERY_KEYS.GROUPS], fetchGroups, restCacheConfig);
};

export const fetchGroupSuggestions = ({ pageParam }) => {
  return RestService.get(`/groups/suggestions?page=${pageParam}`);
};

export const useGroupSuggestionsData = () => {
  return useInfiniteQueryData([QUERY_KEYS.GROUPS_SUGGESTIONS], fetchGroupSuggestions, restCacheConfig);
};

export const fetchGroup = ({ queryKey }: any) => {
  return RestService.get(`/groups/` + queryKey[1]);
};

export const useGroupData = (uuid: string) => {
  return useQueryData([QUERY_KEYS.GROUP, uuid], fetchGroup, restCacheConfig);
};

export const fetchGroupsPosts = ({ queryKey, pageParam }: any) => {
  return RestService.get(`/posts?groupUuid=${queryKey[1]}&page=${pageParam}`);
};

export const useGroupsPostsData = (uuid: any) => {
  return useInfiniteQueryData([QUERY_KEYS.GROUPS_POSTS, uuid], fetchGroupsPosts, restCacheConfig);
};

export const fetchGroupUsers = ({ queryKey, pageParam }: any) => {
  return RestService.get(`/groups/users/${queryKey[1]}?page=${pageParam}`);
};

export const useGroupUsersData = (uuid: string) => {
  return useInfiniteQueryData([QUERY_KEYS.GROUPS_USERS, uuid], fetchGroupUsers, restCacheConfig);
};

const onSuccessAddGroupsData = async (queryClient: any, queryKey: any, newData: any) => {
  await queryClient.cancelQueries({ queryKey });
  const oldData = queryClient.getQueryData(queryKey);
  if (!oldData) return;
  queryClient.setQueryData(queryKey, (oldQueryData: any) => {
    return {
      ...oldQueryData,
      pages: oldQueryData.pages.map((pageData: any, index: number) => {
        return {
          ...pageData,
          data: index === 0 ? [{ ...newData.data, followers: 1, isFollowing: 1 }, ...pageData.data] : pageData.data
        };
      })
    };
  });
};

export const useAddGroupMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ group, formData }: any) => {
      const userUuid = useUserStore.getState().UUID;
      const businessUuid = useUserStore.getState().UBUUID;
      const userData = {
        userUuid,
        businessUuid,
        uuid: uuidv4()
      };
      if (formData) {
        extendFormData(formData, userData);
        return RestService.post('/groups', formData, formData && MULTIPART_HEADERS);
      } else {
        const data = _.assign(group, userData);
        return RestService.post('/groups', data);
      }
    },
    onSuccess: (newData: any) => {
      onSuccessAddGroupsData(queryClient, [QUERY_KEYS.GROUPS], newData);
      onSuccessAddGroupsData(queryClient, [QUERY_KEYS.GROUPS_SUGGESTIONS], newData);
    }
  });
};

const onSuccessUpdateGroupData = async (queryClient: any, queryKey: any, newData: any) => {
  await queryClient.cancelQueries({ queryKey });
  const oldData = queryClient.getQueryData(queryKey);
  if (!oldData) return;
  const newGroup = _.pick(newData.data, ['name', 'about', 'image']);
  queryClient.setQueryData(queryKey, (oldQueryData: any) => {
    if (!oldQueryData?.pages?.length) {
      return {
        ...oldQueryData,
        data: { ...oldQueryData.data, ...newGroup }
      };
    } else {
      return {
        ...oldQueryData,
        pages: oldQueryData.pages.map((pageData: any) => {
          return {
            ...pageData,
            data: pageData.data.map(item => (item.uuid === newData.data.uuid ? { ...item, ...newGroup } : { ...item }))
          };
        })
      };
    }
  });
};

export const useUpdateGroupMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ group, formData }: any) => {
      return RestService.update('/groups/' + group.uuid, formData || group, formData && MULTIPART_HEADERS);
    },
    onSuccess: (newData: any) => {
      onSuccessUpdateGroupData(queryClient, [QUERY_KEYS.GROUPS], newData);
      onSuccessUpdateGroupData(queryClient, [QUERY_KEYS.GROUPS_SUGGESTIONS], newData);
      onSuccessUpdateGroupData(queryClient, [QUERY_KEYS.GROUP, newData.data.uuid], newData);
    }
  });
};

const onSuccessDeleteGroupData = async (queryClient: any, queryKey: any, uuid: any) => {
  await queryClient.cancelQueries({ queryKey });
  const oldData = queryClient.getQueryData(queryKey);
  if (!oldData) return;
  queryClient.setQueryData(queryKey, (oldQueryData: any) => {
    return {
      ...oldQueryData,
      pages: oldQueryData.pages.map((pageData: any) => {
        return {
          ...pageData,
          data: pageData.data.filter(item => item.uuid !== uuid)
        };
      })
    };
  });
};

export const useDeleteGroupMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (uuid: string) => RestService.delete('/groups/' + uuid),
    onSuccess: (newData: any) => {
      onSuccessDeleteGroupData(queryClient, [QUERY_KEYS.GROUPS], newData.data.uuid);
      onSuccessDeleteGroupData(queryClient, [QUERY_KEYS.GROUPS_SUGGESTIONS], newData.data.uuid);
    }
  });
};

const sendFollowGroupData = async (type: string, group: any) => {
  const userUuid = useUserStore.getState().UUID;
  const businessUuid = useUserStore.getState().UBUUID;
  if (isFollowing(type)) {
    return RestService.delete(`actions/remove/${group.uuid}/${userUuid}/${ACTION_TYPES.GROUP_FOLLOW}`);
  } else {
    const data = createAction(ACTION_TYPES.GROUP_FOLLOW, userUuid, businessUuid, group.userUuid, '', '', group.uuid);
    return RestService.post('/actions', data);
  }
};

const onMutateFollowGroupsData = async (queryClient: any, queryKey: any, oldGroup: any, type: string) => {
  await queryClient.cancelQueries({ queryKey });
  const oldData = queryClient.getQueryData(queryKey);
  if (!oldData) return;
  queryClient.setQueryData(queryKey, (oldQueryData: any) => {
    const group = _.cloneDeep(oldGroup);
    group.isFollowing = !isFollowing(type) ? ++group.isFollowing : --group.isFollowing;
    group.followers = !isFollowing(type) ? ++group.followers : --group.followers;
    if (!oldQueryData?.pages?.length) {
      return {
        ...oldQueryData,
        data: group
      };
    } else {
      return {
        ...oldQueryData,
        pages: oldQueryData.pages.map((pageData: any) => {
          return {
            ...pageData,
            data: pageData.data.map(item => (item.uuid === group.uuid ? group : item))
          };
        })
      };
    }
  });
  return { oldData };
};

export const useFollowGroupMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ type, group }: any) => sendFollowGroupData(type, group),
    onMutate: ({ type, group }: any) => {
      onMutateFollowGroupsData(queryClient, [QUERY_KEYS.GROUPS], group, type);
      onMutateFollowGroupsData(queryClient, [QUERY_KEYS.GROUPS_SUGGESTIONS], group, type);
      onMutateFollowGroupsData(queryClient, [QUERY_KEYS.GROUP, group.uuid], group, type);
    }
  });
};

export const useDeleteGroupUsersMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (uuid: string) => RestService.delete('/groups/actions/' + uuid),
    onSuccess: async (newData: any, uuid) => {
      queryClient.setQueryData([QUERY_KEYS.GROUPS_USERS, newData.data.groupUuid], (oldQueryData: any) => {
        return {
          ...oldQueryData,
          pages: oldQueryData.pages.map((pageData: any) => {
            return {
              ...pageData,
              data: pageData.data.filter(item => item.uuid !== uuid)
            };
          })
        };
      });
    }
  });
};

export const useInviteGroupsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (action: any) => {
      const userUuid = useUserStore.getState().UUID;
      const businessUuid = useUserStore.getState().UBUUID;
      const data = _.assign(action, {
        userUuid,
        businessUuid,
        uuid: uuidv4(),
        type: ACTION_TYPES.GROUP_FOLLOW
      });
      return RestService.post('/groups/invite', data);
    },
    onSuccess: (newData: any, data: any) => {
      const oldData = queryClient.getQueryData([QUERY_KEYS.GROUPS_USERS, data.groupUuid]);
      if (!oldData) return;
      queryClient.setQueryData([QUERY_KEYS.GROUPS_USERS, data.groupUuid], (oldQueryData: any) => {
        return {
          ...oldQueryData,
          pages: oldQueryData.pages.map((pageData: any, index: number) => {
            return {
              ...pageData,
              data: index === 0 ? [newData.data, ...pageData.data] : pageData.data
            };
          })
        };
      });
    }
  });
};

export const isMyGroup = (userUuid: string) => {
  const myUuid = useUserStore.getState().UUID;
  return userUuid === myUuid;
};

export const isAdminUser = (users: any, myUuid: string) => users.some((user: any) => user.userUuid === myUuid && user.role === USER_ROLES.ADMIN);
